import { createContext, useContext } from "solid-js";
import type { JSX, ParentComponent } from "solid-js";
import { createStore } from "solid-js/store";
import type { Store } from "solid-js/store";
import { ChatThreadPanel } from "@modules/chats/components/ChatThreadPanel";
import { ChatProfilePanel } from "@modules/chats/components/ChatProfilePanel";
import { BookingDetailPanel } from "@modules/bookings/components/BookingDetailPanel";
import { PaymentDetailPanel } from "@modules/payments/components/PaymentDetailPanel";
import { LocationDetailPanel } from "@modules/locations/components/LocationDetailPanel";
import { RobotDetailPanel } from "@modules/robots/components/RobotDetailPanel";
import { UserDetailPanel } from "@modules/users/components/UserDetailPanel";

export type NavSection =
  | "dashboard"
  | "chats"
  | "bookings"
  | "payments"
  | "locations"
  | "robots"
  | "users"
  | "notifications"
  | "settings";

export type PanelDescriptor = {
  id: string;
  title: string;
  body: () => JSX.Element;
};

type ShellState = {
  activeNav: NavSection;
  panelStack: PanelDescriptor[];
  selectedChatId: string | null;
  selectedUserId: string | null;
  selectedBookingId: string | null;
  selectedPaymentId: string | null;
  selectedLocationId: string | null;
  selectedRobotId: string | null;
};

type ShellActions = {
  setActiveNav: (section: NavSection) => void;
  openPanel: (panel: PanelDescriptor) => void;
  closePanel: (panelId: string) => void;
  resetPanels: () => void;
  selectChat: (chatId: string, userId: string | null) => void;
  selectBooking: (bookingId: string, userId: string | null) => void;
  selectPayment: (paymentId: string, userId: string | null) => void;
  selectLocation: (locationId: string) => void;
  selectRobot: (robotId: string) => void;
  selectUser: (userId: string) => void;
};

type ShellStore = {
  state: Store<ShellState>;
  actions: ShellActions;
};

const ShellStoreContext = createContext<ShellStore>();

const chatsPanelDescriptors = (): PanelDescriptor[] => [
  {
    id: "chat-thread",
    title: "Chat thread",
    body: () => <ChatThreadPanel />,
  },
  {
    id: "chat-profile",
    title: "User context",
    body: () => <ChatProfilePanel />,
  },
];

const bookingPanel: PanelDescriptor = {
  id: "booking-detail",
  title: "Booking detail",
  body: () => <BookingDetailPanel />,
};

const paymentPanel: PanelDescriptor = {
  id: "payment-detail",
  title: "Payment detail",
  body: () => <PaymentDetailPanel />,
};

const locationPanel: PanelDescriptor = {
  id: "location-detail",
  title: "Location detail",
  body: () => <LocationDetailPanel />,
};

const robotPanel: PanelDescriptor = {
  id: "robot-detail",
  title: "Robot detail",
  body: () => <RobotDetailPanel />,
};

const userPanel: PanelDescriptor = {
  id: "user-detail",
  title: "Customer profile",
  body: () => <UserDetailPanel />,
};

const defaultPanelsForSection = (_section: NavSection): PanelDescriptor[] => {
  return [];
};

export const ShellStoreProvider: ParentComponent = (props) => {
  const [state, setState] = createStore<ShellState>({
    activeNav: "dashboard",
    panelStack: [],
    selectedChatId: null,
    selectedUserId: null,
    selectedBookingId: null,
    selectedPaymentId: null,
    selectedLocationId: null,
    selectedRobotId: null,
  });

  const clearSelections = (overrides: Partial<ShellState> = {}) => {
    setState({
      selectedChatId: null,
      selectedUserId: null,
      selectedBookingId: null,
      selectedPaymentId: null,
      selectedLocationId: null,
      selectedRobotId: null,
      ...overrides,
    });
  };

  const ensurePanel = (panel: PanelDescriptor) => {
    setState("panelStack", (panels) => {
      if (panels.some((existing) => existing.id === panel.id)) {
        return panels;
      }
      return [...panels, panel];
    });
  };

  const ensurePanels = (panels: PanelDescriptor[]) => {
    panels.forEach((panel) => ensurePanel(panel));
  };

  const setActiveNav = (section: NavSection) => {
    setState("activeNav", section);
    setState("panelStack", defaultPanelsForSection(section));
    clearSelections();
  };

  const openPanel = (panel: PanelDescriptor) => {
    ensurePanel(panel);
  };

  const closePanel = (panelId: string) => {
    setState("panelStack", (panels) => panels.filter((panel) => panel.id !== panelId));
  };

  const resetPanels = () => setState("panelStack", []);

  const selectChat = (chatId: string, userId: string | null) => {
    clearSelections({ selectedChatId: chatId, selectedUserId: userId });
    ensurePanels(chatsPanelDescriptors());
  };

  const selectBooking = (bookingId: string, userId: string | null) => {
    clearSelections({ selectedBookingId: bookingId, selectedUserId: userId });
    ensurePanel(bookingPanel);
    if (userId) {
      ensurePanel(userPanel);
    }
  };

  const selectPayment = (paymentId: string, userId: string | null) => {
    clearSelections({ selectedPaymentId: paymentId, selectedUserId: userId });
    ensurePanel(paymentPanel);
    if (userId) {
      ensurePanel(userPanel);
    }
  };

  const selectLocation = (locationId: string) => {
    clearSelections({ selectedLocationId: locationId });
    ensurePanel(locationPanel);
  };

  const selectRobot = (robotId: string) => {
    clearSelections({ selectedRobotId: robotId });
    ensurePanel(robotPanel);
  };

  const selectUser = (userId: string) => {
    clearSelections({ selectedUserId: userId });
    ensurePanel(userPanel);
  };

  const value: ShellStore = {
    state,
    actions: {
      setActiveNav,
      openPanel,
      closePanel,
      resetPanels,
      selectChat,
      selectBooking,
      selectPayment,
      selectLocation,
      selectRobot,
      selectUser,
    },
  };

  return <ShellStoreContext.Provider value={value}>{props.children}</ShellStoreContext.Provider>;
};

export const useShellStore = (): ShellStore => {
  const context = useContext(ShellStoreContext);
  if (!context) {
    throw new Error("useShellStore must be used within ShellStoreProvider");
  }
  return context;
};
